import {
  type ChatMessageList,
  OfChatMessagesService,
  type OnlyFansMessage,
} from '@api'
import {
  attach,
  combine,
  createEffect,
  createEvent,
  createStore,
} from 'effector'
import _ from 'lodash/fp'

import { DIALOG_TYPE } from '@/dialogs/constants.ts'
import { $fanId } from '@/models/chattingUI/chats/chats.ts'
import { $messagesByIdMap } from '@/models/chattingUI/messages/messages.ts'
import { $creatorId } from '@/models/creators.ts'
import { $openedDialogs } from '@/models/dialogs.ts'
import type { MessageItem } from '@/modules/chatting/types.ts'
import { handleError } from '@/utils/error'

const noMessages: unknown[] = []

export const setPinnedMessageIdToFetch = createEvent<Nullable<number>>(
  'set message id to fetch',
)
export const $pinnedMessageIdToFetch = createStore<Nullable<number>>(null, {
  name: 'message id to fetch',
}).on(
  setPinnedMessageIdToFetch,
  (_, messageIdToFetch) => messageIdToFetch ?? null,
)

export const pinMessageFx = attach({
  name: 'pin fan message',
  source: {
    userId: $fanId,
    creatorId: $creatorId,
  },
  mapParams: (messageId: number, { creatorId, userId }) => ({
    messageId,
    creatorId: creatorId!,
    userId,
  }),
  effect: createEffect(
    async (params: {
      messageId: number
      creatorId: number
      userId: number
    }) => {
      const result = await OfChatMessagesService.pinChatMessage(params)

      return {
        fanId: params.userId,
        message: result,
      }
    },
  ),
})

pinMessageFx.failData.watch((error) => {
  handleError(
    error,
    'Unexpected error occurred while pinning message, please try again later',
  )
})

export const unpinMessageFx = attach({
  name: 'UNpin fan message',
  source: {
    userId: $fanId,
    creatorId: $creatorId,
  },
  mapParams: (messageId: number, { creatorId, userId }) => ({
    messageId,
    creatorId: creatorId!,
    userId,
  }),
  effect: createEffect(
    async (params: {
      messageId: number
      creatorId: number
      userId: number
    }) => {
      const result = await OfChatMessagesService.unpinChatMessage(params)

      return {
        fanId: params.userId,
        message: result,
      }
    },
  ),
})

unpinMessageFx.failData.watch((error) => {
  handleError(
    error,
    'Unexpected error occurred while unpinning message, please try again later',
  )
})

export const getPinnedMessagesFx = attach({
  name: 'get pinned fan messages',
  source: {
    creatorId: $creatorId,
  },
  mapParams: (
    { fanId, anchorMessageId }: { anchorMessageId: number; fanId: number },
    { creatorId },
  ) => ({
    anchorMessageId,
    userId: fanId,
    creatorId: creatorId!,
  }),
  effect: createEffect(OfChatMessagesService.getPinnedMessages),
})

export const resetPinnedMessages = createEvent('reset messages by fan id')
/**
 * Selected fan pinned message pages.
 */
export const $pinnedMessagePages = createStore<ChatMessageList[]>(
  noMessages as ChatMessageList[],
  {
    name: 'pinned message pages by fan id map',
  },
).reset(resetPinnedMessages)

export const $canLoadNextMessagePages = combine(
  $pinnedMessagePages,
  getPinnedMessagesFx.pending,
  (pages, isLoading) => {
    return (pages.length ? pages[pages.length - 1].hasMore : true) && !isLoading
  },
)

export const getConcretePinnedMessagePageFx = attach({
  name: 'get fan concrete pinned messages page',
  source: {
    fanId: $fanId,
    pages: $pinnedMessagePages,
  },
  mapParams: (anchorMessageId: number, { fanId }) => ({
    anchorMessageId,
    fanId,
  }),
  effect: getPinnedMessagesFx,
})

export const getNextPinnedMessagePageFx = attach({
  name: "get fan pinned messages' next page",
  source: {
    fanId: $fanId,
    pages: $pinnedMessagePages,
  },
  mapParams: (_: void, { pages, fanId }) => {
    const lastPageMessages = pages[pages.length - 1]?.list

    return {
      anchorMessageId:
        lastPageMessages?.[lastPageMessages?.length - 1]?.id ?? 0,
      fanId,
    }
  },
  effect: getPinnedMessagesFx,
})

export const addPinnedMessageByFanId = createEvent<OnlyFansMessage>(
  'add pinned message by fan id',
)
/**
 * Pinned fan messages.
 */
export const $pinnedMessages = createStore<MessageItem[]>(
  noMessages as MessageItem[],
  {
    name: 'pinned messages by fan id map',
  },
)
  .on(getPinnedMessagesFx.doneData, (store, { list }) => {
    const allMessages = store.concat(
      list.map(({ id, createdAt }) => ({
        id,
        createdAt: new Date(createdAt).getTime(),
      })),
    )

    return Object.values(
      allMessages.reduce<Record<number, MessageItem>>((result, newMessage) => {
        result[newMessage.id] = newMessage

        return result
      }, {}),
    ).sort((a, b) => a.createdAt - b.createdAt)
  })
  .on(addPinnedMessageByFanId, (store, { id, createdAt }) => {
    const allMessages = store.concat({
      id,
      createdAt: new Date(createdAt).getTime(),
    })

    return Object.values(
      allMessages.reduce<Record<number, MessageItem>>((result, newMessage) => {
        result[newMessage.id] = newMessage

        return result
      }, {}),
    ).sort((a, b) => a.createdAt - b.createdAt)
  })
  .on(unpinMessageFx.doneData, (store, { message }) => {
    return store.filter((m) => m.id !== message.id)
  })
  .reset(resetPinnedMessages)

export const $groupedPinnedMessagesEntries = combine(
  $pinnedMessages,
  $messagesByIdMap,
  (messages, messagesByIdMap) => {
    const dtos = messages.map(({ id }) => messagesByIdMap[id]).filter(Boolean)

    return Object.entries(_.groupBy('sentDate', dtos))
  },
)

export const $isPinnedDialogOpen = $openedDialogs.map((dialogs) => {
  return dialogs.some((dialog) => dialog.type === DIALOG_TYPE.PINNED_MESSAGES)
})
